// Global network retry bootstrap
// - Wraps window.fetch with fetch-retry
// - Adds axios response interceptor to retry on transient errors (429, 5xx, DNS/Network)

/* eslint-disable no-undef */

import axios from "axios";

// Support both browser and test environments
const globalScope = typeof window !== "undefined" ? window : globalThis;

// ---- Fetch retry wrapper ----
try {
	// Only patch once
	if (!globalScope.__WB32_FETCH_RETRY_PATCHED__) {
		const createFetchRetry = require("fetch-retry");
		const originalFetch = globalScope.fetch ? globalScope.fetch.bind(globalScope) : null;
		if (originalFetch && !globalScope.__WB32_FETCH_ORIGINAL__) {
			globalScope.__WB32_FETCH_ORIGINAL__ = originalFetch;
		}

		if (originalFetch) {
			const baseBackoffMs = 750; // modest base backoff; exponential applied per attempt

			const shouldRetry = (attempt, error, response) => {
				// Never retry AbortErrors (signals that request was intentionally cancelled)
				if (error && (error.name === 'AbortError' || error.constructor.name === 'AbortError')) {
					return false;
				}

				// Retry on 429, 408, and 5xx
				if (response) {
					const { status } = response;
					if (status === 429 || status === 408) return true;
					if (status >= 500) return true;
				}

				// Retry on network/DNS errors (browser gives a TypeError)
				if (error) {
					const message = String(error && (error.message || error.toString())).toLowerCase();
					
					// Don't retry if it's an abort-related error message
					if (message.includes("abort")) {
						return false;
					}
					
					if (
						message.includes("failed to fetch") ||
						message.includes("networkerror") ||
						message.includes("network error") ||
						message.includes("err_name_not_resolved") ||
						message.includes("enotfound") ||
						message.includes("dns") ||
						message.includes("timeout")
					) {
						return true;
					}
				}

				return false;
			};

			const computeDelay = (attempt, error, response) => {
				// Honour Retry-After header when present
				if (response && (response.status === 429 || response.status === 503)) {
					const retryAfter = response.headers && response.headers.get && response.headers.get("Retry-After");
					if (retryAfter) {
						const seconds = Number(retryAfter);
						if (!Number.isNaN(seconds) && seconds >= 0) {
							return seconds * 1000;
						}
					}
				}

				// Exponential backoff with jitter
				const jitter = Math.floor(Math.random() * 250);
				return Math.min(10000, Math.pow(2, attempt) * baseBackoffMs + jitter);
			};

			const fetchWithRetryFactory = createFetchRetry(originalFetch);

			// Patch global fetch to apply sensible defaults while allowing per-call overrides
			globalScope.fetch = (input, init = {}) => {
				const urlString = typeof input === "string" ? input : (input && input.url) ? input.url : "";
				const method = (init && init.method ? String(init.method) : "GET").toUpperCase();
				const bodyStr = typeof init.body === "string" ? init.body : "";

				// Detect ethers.js network detection attempts specifically
				const isJsonRpc = method === "POST" && bodyStr.includes('"jsonrpc"');
				const isEthersNetworkDetection = isJsonRpc && 
					(bodyStr.includes('"net_version"') || 
					 bodyStr.includes('"eth_chainId"') || 
					 bodyStr.includes('"eth_getBlockByNumber"') ||
					 bodyStr.includes('"web3_clientVersion"') ||
					 bodyStr.includes('"eth_getBalance"') ||
					 bodyStr.includes('"eth_getTransactionCount"') ||
					 bodyStr.includes('"eth_gasPrice"') ||
					 bodyStr.includes('"eth_blockNumber"'));

				// `WebAssembly.instantiateStreaming(fetch(url))` keeps
				// the response body open for the full duration of the
				// WASM compile (which on a cold CPU can easily exceed
				// our 15s default timeout). Aborting the controller
				// then surfaces as
				//   "AbortError: signal is aborted without reason"
				//   "both async and sync fetching of the wasm failed"
				// even though the network round-trip succeeded — the
				// streaming compile gets cancelled mid-stream. We also
				// skip retries here: re-fetching a megabyte of WASM
				// just to hit the same compile-abort wastes bandwidth.
				const headerHint = (init?.headers?.Accept || init?.headers?.accept || '').toString();
				const isWasmRequest = /\.wasm($|\?)/i.test(urlString)
					|| headerHint.toLowerCase().includes('application/wasm');

				// Block problematic hosts for any request that would trigger retries
				const problemHosts = ['arb1.winbit32.com'];
				try {
					const url = new URL(urlString, typeof location !== "undefined" ? location.href : undefined);
					if (problemHosts.includes(url.host)) {
						// For network detection, block completely
						if (isEthersNetworkDetection) {
							console.log(`🚫 Blocking ethers.js network detection to known failing host: ${url.host} - Method: ${bodyStr.match(/"method":"([^"]+)"/)?.[1] || 'unknown'}`);
							return Promise.reject(new Error(`Network detection blocked for ${url.host}`));
						}
						// For other requests, fail fast with a 404-like error to avoid retries
						else if (isJsonRpc) {
							console.log(`⚡ Fast-failing JSON-RPC to known failing host: ${url.host} - Method: ${bodyStr.match(/"method":"([^"]+)"/)?.[1] || 'unknown'}`);
							return Promise.resolve(new Response('{"jsonrpc":"2.0","error":{"code":-32603,"message":"Host unavailable"},"id":1}', {
								status: 200, // Return 200 with error to avoid retries
								headers: { 'Content-Type': 'application/json' }
							}));
						}
					}
				} catch (urlError) {
					// If URL parsing fails, check if the string contains problematic hosts
					if (problemHosts.some(host => urlString.includes(host))) {
						if (isEthersNetworkDetection) {
							console.log(`🚫 Blocking ethers.js network detection to suspected failing host in: ${urlString}`);
							return Promise.reject(new Error(`Network detection blocked for suspected host`));
						} else if (isJsonRpc) {
							console.log(`⚡ Fast-failing JSON-RPC to suspected failing host in: ${urlString}`);
							return Promise.resolve(new Response('{"jsonrpc":"2.0","error":{"code":-32603,"message":"Host unavailable"},"id":1}', {
								status: 200,
								headers: { 'Content-Type': 'application/json' }
							}));
						}
					}
				}

				// If caller supplied a `retries` option, assume an outer wrapper (like fetch-retry) is orchestrating
				// and disable inner retries to avoid multiplicative backoffs.
				let retries;
				if (typeof init.retries === "number") {
					retries = 0;
				} else if (isEthersNetworkDetection) {
					retries = 0;
				} else if (isWasmRequest) {
					// WASM streaming compile failures are rarely
					// fixed by a re-fetch and we don't want to waste
					// the user's bandwidth re-downloading megabytes.
					retries = 0;
				} else if (/coingecko\.com/i.test(urlString) || /\/api\/v3\/coins\//i.test(urlString)) {
					// Free-tier CoinGecko 429s get worse with retries;
					// callers should throttle / cooldown instead.
					retries = 0;
				} else {
					retries = 2;
				}
				const retryOn = init.retryOn || (isEthersNetworkDetection ? (() => false) : shouldRetry);
				const retryDelay = init.retryDelay || computeDelay;

				// Optional request timeout via AbortController. WASM
				// streaming compiles get NO automatic timeout — they
				// hold the response body open for the entire compile
				// step, so any default would fire before the module is
				// ready. The caller can still pass an explicit
				// `init.timeout` if they really want to bound it.
				let controller;
				let timeoutId;
				let timeoutMs;
				if (typeof init.timeout === "number") {
					timeoutMs = init.timeout;
				} else if (isEthersNetworkDetection) {
					timeoutMs = 5000;
				} else if (isWasmRequest) {
					timeoutMs = 0;
				} else {
					timeoutMs = 15000;
				}
				const useAbortTimeout = timeoutMs > 0 && typeof AbortController !== "undefined";
				const mergedInit = { ...init, retries, retryOn, retryDelay };

				if (useAbortTimeout) {
					controller = new AbortController();
					const originalSignal = init.signal;
					if (originalSignal) {
						// If caller provided a signal, abort when either aborts
						originalSignal.addEventListener("abort", () => controller.abort());
					}
					mergedInit.signal = controller.signal;
					timeoutId = setTimeout(() => controller.abort(), timeoutMs);
				}

				return fetchWithRetryFactory(input, mergedInit).finally(() => {
					if (timeoutId) clearTimeout(timeoutId);
				});
			};

			globalScope.__WB32_FETCH_RETRY_PATCHED__ = true;
		}
	}
} catch (e) {
	console.warn("WINBIT32: Failed to patch global fetch with retry:", e);
}

// ---- Axios retry interceptor ----
try {
	if (!axios.__WB32_AXIOS_RETRY_PATCHED__) {
		const maxDefaultRetries = 2; // match fetch default

		const shouldRetryAxios = (error) => {
			// Never retry AbortErrors (signals that request was intentionally cancelled)
			if (error && (error.name === 'AbortError' || error.constructor.name === 'AbortError')) {
				return false;
			}

			const response = error && error.response;
			if (response) {
				const { status } = response;
				if (status === 429 || status === 408) return true;
				if (status >= 500) return true;
			}
			const message = String(error && (error.message || error.toString())).toLowerCase();
			
			// Don't retry if it's an abort-related error message
			if (message.includes("abort")) {
				return false;
			}
			
			return (
				message.includes("network error") ||
				message.includes("timeout") ||
				message.includes("enotfound") ||
				message.includes("dns") ||
				message.includes("err_name_not_resolved")
			);
		};

		axios.interceptors.response.use(
			(response) => response,
			async (error) => {
				const config = error && error.config ? error.config : {};
				if (!config) return Promise.reject(error);

				config.__retryCount = (config.__retryCount || 0);
				const maxRetries = typeof config.retry === "number" ? config.retry : maxDefaultRetries;

				if (config.__retryCount >= maxRetries || !shouldRetryAxios(error)) {
					return Promise.reject(error);
				}

				config.__retryCount += 1;

				// Compute backoff with jitter
				const base = 750;
				const delayMs = Math.min(10000, Math.pow(2, config.__retryCount) * base + Math.floor(Math.random() * 250));

				await new Promise((resolve) => setTimeout(resolve, delayMs));
				return axios(config);
			}
		);

		axios.__WB32_AXIOS_RETRY_PATCHED__ = true;
	}
} catch (e) {
	console.warn("WINBIT32: Failed to set up axios retry interceptor:", e);
}

// Export a no-op to make this module importable without side-effects in tests
export const __networkRetryInitialised = true;


